import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import styles from "../RightSidebar/rightsidebar.module.css";
import { ImageLink } from "../Buttons/ImageLink";
import BackBtn from "../../Assets/images/back.svg";
import { useGlobalContext } from "../../Context/GlobalContext";
import PrimaryButton from "../Buttons/PrimaryButton";
import BookingPaymentDetails from "../BookingPaymentDetails/BookingPaymentDetails";
import { getCustomerCreditDetails, getPaymentDetails, getMembershipPaymentDetails } from "../../APICalling/APICalling";
import { roundCashAmount } from "../../Utils/utils";
import CustomerCreditDetails from "../CustomerCreditDetails/CustomerCreditDetails";
import PaymentMethods from "../PaymentMethods/PaymentMethods";
import {
	buildOpenMembershipSaleUrl,
	findOpenMembershipSaleOrPendingTransactionForCustomer,
} from "../../Utils/openMembershipSale";
import { toast } from "react-toastify";
import { ERROR_MESSAGES } from "../../Utils/Constants/errorMessages";
import ReloadIcon from "../Icons/ReloadIcon";

/** Normalizes customer credit API response across flat/nested success shapes. */
const parseCustomerCreditResponse = (response) => {
	if (!response || typeof response !== "object") {
		return [];
	}
	const payload = response.success !== undefined ? response : response.data;
	if (!payload || payload.success !== true) {
		return [];
	}
	const details = payload.data;
	if (Array.isArray(details)) {
		return details;
	}
	if (details && typeof details === "object") {
		return [details];
	}
	return [];
};

const parseOptionalAmount = (value) => {
	if (value === null || value === undefined || value === "") {
		return null;
	}
	const amount = Number(value);
	return Number.isFinite(amount) ? amount : null;
};

const getFirstAvailableAmount = (...values) => {
	for (const value of values) {
		const amount = parseOptionalAmount(value);
		if (amount !== null) return amount;
	}
	return null;
};

const PaymentPage = ({ 
	onBack, 
	onNext,  
	onReload = null,
	completeBooking, 
	setCurrentStep, 
	bookingDataFromAPI: propBookingData,
  	hideHeader = false,
  	isProductSale = false,
	isMembershipPayment = false,
	gstRate = 9,
	productGstAmount = null,
	onTopUpClick = null,
	customerCreditRefreshKey = 0,
	paymentMethodsRefreshKey = 0,
	creditFetchUserId = null,
	onOpenMembershipSale = null,
	embeddedMembershipCheckout = false,
	embeddedMembershipHomePage = false,
}) => {
	const {
		serviceTotal,
		paymentSummary,
		setPaymentSummary,
		amountDue,
		setAmountDue,
		setCurrentPaymentMethodId,
		setCurrentPaymentMethod,
		setCurrentPaymentMethodTitle,
		setAmountToCharge,
		bookingDetails,
		customerCreditDetails,
		setCustomerCreditDetails,
		selectedOutlet,
		loggedOutletID,
	} = useGlobalContext();

	const bookingDataFromAPI = propBookingData || null;
	const paymentDetailsBookingId = isProductSale? bookingDataFromAPI?.booking_id || bookingDataFromAPI?.id || null : bookingDetails?.id || null;
	const resolvedSaleId = bookingDataFromAPI?.sale_id ?? bookingDetails?.sale_id ?? null;
	const resolvedPaymentSaleId = isProductSale ? paymentDetailsBookingId : resolvedSaleId;
	const paymentQueryParams = useMemo(() => {
		if (!isProductSale || !paymentDetailsBookingId) return undefined;
		return { sale_id: paymentDetailsBookingId };
	}, [isProductSale, paymentDetailsBookingId]);

	const isAlreadyVoided = bookingDetails?.status?.toLowerCase() === 'checked-in' && bookingDetails?.cancel_reason !== null;
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isProcessingPayment, setIsProcessingPayment] = useState(false);
	const [isLoadingPaymentDetails, setIsLoadingPaymentDetails] = useState(false);
	const [paymentDetails, setPaymentDetails] = useState([]);
	const [enteredChargeAmount, setEnteredChargeAmount] = useState("");
	const [amountInputError, setAmountInputError] = useState("");
	const chargeAmountInputRef = useRef(null);
	const prevLoadingPaymentDetailsRef = useRef(false);

	const focusChargeAmountInput = useCallback(() => {
		// Double rAF: wait until React commits the latest amount (e.g. after
		// partial-payment amountDue recalculation) before focus + select.
		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				const input = chargeAmountInputRef.current;
				if (!input) return;
				input.focus({ preventScroll: true });
				if (input.value) {
					input.select();
				}
			});
		});
	}, []);
	const [loadedCreditUserId, setLoadedCreditUserId] = useState(null);
	const [insufficientCreditMessage, setInsufficientCreditMessage] = useState("");
	const [completeBookingError, setCompleteBookingError] = useState("");
	const completeBookingErrorResponseRef = useRef(null);
	const [openMembershipSale, setOpenMembershipSale] = useState(null);
	const parsedAmountDue = parseFloat(amountDue);
	const maxChargeAmount = isNaN(parsedAmountDue) ? 0 : Math.max(parsedAmountDue, 0);
	const parsedEnteredCharge = parseFloat(enteredChargeAmount);
	const hasAmountInput = enteredChargeAmount.trim() !== "";
	const hasValidEnteredAmount =
		hasAmountInput &&
		!isNaN(parsedEnteredCharge) &&
		parsedEnteredCharge >= 0 &&
		(maxChargeAmount === 0
			? parsedEnteredCharge <= maxChargeAmount + 0.001
			: parsedEnteredCharge > 0 && parsedEnteredCharge <= maxChargeAmount + 0.001);


	const resolvedCreditUserId = creditFetchUserId ?? bookingDetails?.user?.id ?? null;

	const handleOpenMembershipSale = useCallback((sale = openMembershipSale) => {
		if (!sale) return;
		if (typeof onOpenMembershipSale === "function") {
			onOpenMembershipSale(sale);
			return;
		}
		const url = buildOpenMembershipSaleUrl(sale);
		if (!url) {
			toast.error("Could not find the open membership sale.");
			return;
		}
		window.open(url, "_blank", "noopener,noreferrer");
	}, [onOpenMembershipSale, openMembershipSale]);

	useEffect(() => {
		const userId = resolvedCreditUserId;
		if (!userId) {
			setCustomerCreditDetails([]);
			setLoadedCreditUserId(null);
			return;
		}
		let cancelled = false;
		const fetchCustomerCreditDetails = async () => {
			try {
				const response = await getCustomerCreditDetails(userId);
				const data = parseCustomerCreditResponse(response);
				if (cancelled) return;
				setCustomerCreditDetails(data);
				setLoadedCreditUserId(userId);
			} catch (error) {
				console.error("Error fetching customer credit details:", error);
				if (cancelled) return;
				setCustomerCreditDetails([]);
				setLoadedCreditUserId(userId);
			}
		};
		fetchCustomerCreditDetails();
		return () => {
			cancelled = true;
		};
	}, [resolvedCreditUserId, setCustomerCreditDetails, customerCreditRefreshKey]);

	useEffect(() => {
		if (!resolvedCreditUserId || isMembershipPayment) {
			setOpenMembershipSale(null);
			return;
		}
		// Wait until the credit request finishes, then check by user id.
		// Suspended partial memberships can have sparse credit rows, so the
		// pending-transaction helper remains the source of truth.
		if (loadedCreditUserId !== resolvedCreditUserId) return;
		let cancelled = false;
		const fetchOpenMembershipSale = async () => {
			try {
				const membershipDetail =
					customerCreditDetails?.[0]?.membership_detail ||
					customerCreditDetails?.[0]?.membershipDetail ||
					customerCreditDetails?.[0] ||
					null;
				const sale = await findOpenMembershipSaleOrPendingTransactionForCustomer(
					resolvedCreditUserId,
					selectedOutlet || loggedOutletID || null,
					membershipDetail
				);
				if (!cancelled) {
					setOpenMembershipSale(sale);
				}
			} catch (error) {
				console.error("Error fetching open membership sale:", error);
				if (!cancelled) {
					setOpenMembershipSale(null);
				}
			}
		};

		fetchOpenMembershipSale();
		return () => {
			cancelled = true;
		};
	// eslint-disable-next-line react-hooks/exhaustive-deps -- customerCreditDetails read from current value; loadedCreditUserId is the trigger
	}, [
		resolvedCreditUserId,
		loadedCreditUserId,
		isMembershipPayment,
		selectedOutlet,
		loggedOutletID,
		paymentMethodsRefreshKey,
		customerCreditRefreshKey,
	]);

	const isCustomerCreditLoading =
		!!resolvedCreditUserId && loadedCreditUserId !== resolvedCreditUserId;



	useEffect(() => {
		if (maxChargeAmount > 0) {
			const nextDefaultAmount = maxChargeAmount.toFixed(2);
			setEnteredChargeAmount(nextDefaultAmount);
			setAmountToCharge(nextDefaultAmount);
			setAmountInputError("");
			// Re-select after amountDue updates (partial payments recalculate remaining)
			focusChargeAmountInput();
			return;
		}
		setEnteredChargeAmount("");
		setAmountToCharge("");
		setAmountInputError("");
	}, [maxChargeAmount, setAmountToCharge, focusChargeAmountInput]);

	useEffect(() => {
		focusChargeAmountInput();
	}, [focusChargeAmountInput]);

	useEffect(() => {
		const wasLoading = prevLoadingPaymentDetailsRef.current;
		prevLoadingPaymentDetailsRef.current = isLoadingPaymentDetails;
		if (wasLoading && !isLoadingPaymentDetails && maxChargeAmount > 0) {
			focusChargeAmountInput();
		}
	}, [isLoadingPaymentDetails, maxChargeAmount, focusChargeAmountInput]);

	const isZeroPaymentTotal = parseFloat(paymentSummary?.total) === 0;

	const validateEnteredAmount = (value) => {
		const trimmed = value.trim();
		if (!trimmed) {
			return isZeroPaymentTotal ? "" : ERROR_MESSAGES.ENTER_AMOUNT_TO_CHARGE;
		}
		const parsed = parseFloat(trimmed);
		if (isNaN(parsed) || parsed < 0) {
			return ERROR_MESSAGES.ENTER_VALID_AMOUNT;
		}
		if (maxChargeAmount === 0) {
			if (parsed > maxChargeAmount + 0.001) {
				return "Payment amount cannot exceed total payable amount.";
			}
			return "";
		}
		if (parsed <= 0) {
			return "Enter a valid amount.";
		}
		if (parsed > maxChargeAmount + 0.001) {
			return "Payment amount cannot exceed total payable amount.";
		}
		return "";
	};

	// Fetch payment details from BookingPaymentDetails
	useEffect(() => {
		const fetchPaymentDetails = async () => {
			try {
				const effectiveSaleId = isMembershipPayment ? resolvedSaleId : resolvedPaymentSaleId;
				if (!paymentDetailsBookingId || !effectiveSaleId) {
					setPaymentDetails([]);
					setIsLoadingPaymentDetails(false);
					return;
				}
				setIsLoadingPaymentDetails(true);
				const response = isMembershipPayment
					? await getMembershipPaymentDetails(resolvedSaleId)
					: await getPaymentDetails(resolvedPaymentSaleId, paymentQueryParams);
				const data = response?.data?.data || [];
				setPaymentDetails(data);
			} catch (error) {
				console.error("Error fetching payment details:", error);
				setPaymentDetails([]);
			} finally {
				setIsLoadingPaymentDetails(false);
			}
		};
		fetchPaymentDetails();
	}, [paymentDetailsBookingId, resolvedSaleId, resolvedPaymentSaleId, isMembershipPayment, paymentQueryParams, paymentMethodsRefreshKey, customerCreditRefreshKey]);

	useEffect(() => {
		const timer = setTimeout(() => {
			const productGstOverride = isProductSale
				? parseOptionalAmount(productGstAmount ?? bookingDataFromAPI?.gst_amount)
				: null;
			const membershipTotalOverride = isMembershipPayment
				? getFirstAvailableAmount(
					bookingDataFromAPI?.membership_total,
					bookingDataFromAPI?.amount,
					bookingDataFromAPI?.purchase_amount,
					bookingDataFromAPI?.grand_total,
					bookingDataFromAPI?.sub_total,
					paymentDetails?.[0]?.membership_total,
					paymentDetails?.[0]?.transaction_amount,
					paymentDetails?.[0]?.purchase_amount,
					paymentDetails?.[0]?.grand_total,
					paymentDetails?.[0]?.sub_total
				)
				: null;
			const gstRateEffective = isMembershipPayment ? 0 : gstRate;
			const gst = productGstOverride !== null
				? productGstOverride
				: ((membershipTotalOverride ?? serviceTotal) * gstRateEffective) / 100;
			const subtotal = membershipTotalOverride ?? serviceTotal;
			const total = subtotal + gst;
			const midtotal = subtotal + gst;

			setPaymentSummary((prev) => ({
				...prev,
				subtotal,
				midtotal,
				gst,
				total: total.toFixed(2),
			}));

			
			// Calculate paid amount from BookingPaymentDetails - sum all amount_value
			const paid = paymentDetails
				.reduce((sum, payment) => {
					const amountValue = parseFloat(payment?.amount_value || 0);
					return sum + (isNaN(amountValue) ? 0 : amountValue);
				}, 0);
			
			// Check if there are any CASH payments in the payment history
			// const hasCashPayments = paymentDetails.some(payment => 
			// 	payment?.payment_method?.toUpperCase() === "CASH"
			// );

			// If there are CASH payments, round the total to match the CASH rounding that was applied to payments
			let effectiveTotal = total;
			// if (hasCashPayments) {
			// 	effectiveTotal = roundCashAmount(total);
			// }
			const calculatedAmountDue = (effectiveTotal - paid);
			setAmountDue(calculatedAmountDue.toFixed(2));
		},);

		return () => clearTimeout(timer);
		}, [
			serviceTotal,
			paymentDetails,
			setAmountDue,
			setPaymentSummary,
			bookingDetails?.deposit,
			isProductSale,
			isMembershipPayment,
			gstRate,
			productGstAmount,
			bookingDataFromAPI?.gst_amount,
			bookingDataFromAPI?.membership_total,
			bookingDataFromAPI?.amount,
			bookingDataFromAPI?.purchase_amount,
			bookingDataFromAPI?.grand_total,
			bookingDataFromAPI?.sub_total,
		]);

	const getCompleteBookingErrorMessage = (payload) => {
		if (!payload) return "";
		const message = payload?.message || payload?.data?.message || payload?.response?.data?.message || "";
		return typeof message === "string" ? message.trim() : "";
	};
	const handleCompleteBooking = async () => {
		if (isSubmitting) return;
		setIsSubmitting(true);
		setCompleteBookingError("");
		completeBookingErrorResponseRef.current = null;
		const due = parseFloat(amountDue);
		try {
			const response = await completeBooking({method: null,amount: due,partial: false,});
			const isSuccess = response?.success === true || response?.data?.success === true;
			if (!isSuccess) {
				completeBookingErrorResponseRef.current = response;
				setCompleteBookingError(getCompleteBookingErrorMessage(response) || "Failed to complete booking. Please try again.");
			}
		} catch (error) {
			completeBookingErrorResponseRef.current = error;
			setCompleteBookingError(getCompleteBookingErrorMessage(error) || error?.message || "Failed to complete booking. Please try again.");
		} finally {
			setIsSubmitting(false);
		}
	};


	return (
	<>
		<div
			className={`${styles.paymentPage} h-100 ${embeddedMembershipCheckout ? styles.membershipEmbeddedPayment : ''} ${embeddedMembershipCheckout && embeddedMembershipHomePage ? styles.membershipEmbeddedPaymentHome : ''}`}
		>
		
			<div className={styles.rightHeader}>
				<div className={styles.dFlex}>
					<ImageLink
						imageSrc={BackBtn}
						altText="Back"
						handleClick={onBack}
					/>
					<h5>Payments</h5>
				</div>
			</div>
			<div className={`${styles.infoWrapper} ${styles.paymentContent}`}>
				<div className={styles.sidebarContainer}>
					<div className={styles.sidebarRow}>
						<div className={`${styles.sidebarCellLeft} ${styles.sidebarText}`}>
							Subtotal
						</div>
						<div className={`${styles.sidebarCellRight} ${styles.sidebarText}`}>
							${paymentSummary?.subtotal?.toFixed(2)}
						</div>
					</div>
					{!isMembershipPayment && (
						<div className={styles.sidebarRow}>
							<div className={`${styles.sidebarCellLeft} ${styles.sidebarText}`}>
								GST
							</div>
							<div className={`${styles.sidebarCellRight} ${styles.sidebarText}`}>
								${parseFloat(paymentSummary.gst)?.toFixed(2)}
							</div>
						</div>
					)}
					<div className={styles.sidebarRow}>
						<div className={`${styles.sidebarCellLeft} ${styles.sidebarTextBold}`}>
							Total
						</div>
						<div className={`${styles.sidebarCellRight} ${styles.sidebarTextBold}`}>
							${parseFloat(paymentSummary.total).toFixed(2)}
						</div>
					</div>
					{/* {bookingDetails?.panel === "customer" && 
						<div className={styles.sidebarRow}>
							<div className={`${styles.sidebarCellLeft} ${styles.sidebarText}`}>
								Online Deposit Amount
							</div>
							<div className={`${styles.sidebarCellRight} ${styles.sidebarText}`}>
								${parseFloat(bookingDetails?.deposit || 0).toFixed(2)}
							</div>
						</div>
					} */}
				</div>
				<hr className={styles.divider} />

				{paymentDetails.length > 0 && (
					<>
						{!isAlreadyVoided && (
							<BookingPaymentDetails
								bookingID={paymentDetailsBookingId}
								saleId={resolvedPaymentSaleId}
								from="sale-booking-info"
								paymentDetailsFromPaymentPage={paymentDetails}
								loadingFromPaymentPage={isLoadingPaymentDetails}
								remainingBalance={amountDue}
							/>
						)}
						<div className={`${styles.sidebarContainer} ${styles.sidebarContainerRemoveTopSpace}`}>
							<div className={styles.sidebarRow}>
								<div className={`${styles.sidebarCellLeft} ${styles.sidebarTextBold}`}>Remaining Balance</div>
								<div className={`${styles.sidebarCellRight} ${styles.sidebarTextBold}`}>
									${amountDue}
								</div>
							</div>
						</div>
						<hr className={styles.divider} />
					</>
				)}
				
				<div className={styles.sidebarContainer}>
					<div className={styles.sidebarRow}>
						<div className={`${styles.sidebarCellLeft} ${styles.sidebarText}`}>Amount to Charge</div>
						<div className={`${styles.sidebarCellRight} ${styles.sidebarText}`}>
							<div className={styles.tipInputWrapper}>
								<span className={styles.dollarPrefix}>$</span>
								<input
									ref={chargeAmountInputRef}
									type="text"
									className={styles.tipInput}
									placeholder="0.00"
									value={enteredChargeAmount}
									onFocus={(e) => {
										if (e.target.value) {
											e.target.select();
										}
									}}
									onChange={(e) => {
										const val = e.target.value;
										if (!/^\d*\.?\d{0,2}$/.test(val)) return;
										setEnteredChargeAmount(val);
										setCurrentPaymentMethod("");
										setCurrentPaymentMethodTitle("");
										setCurrentPaymentMethodId(null);
										setAmountToCharge(val);
										if (amountInputError) {
											setAmountInputError(validateEnteredAmount(val));
										}
									}}
									onBlur={(e) => {
										const val = e.target.value.trim();
										if (!val) {
											setAmountToCharge("");
											setAmountInputError(
												isZeroPaymentTotal ? "" : ERROR_MESSAGES.ENTER_AMOUNT_TO_CHARGE
											);
											return;
										}
										const parsed = parseFloat(val);
										if (isNaN(parsed)) {
											setAmountToCharge("");
											setAmountInputError(ERROR_MESSAGES.ENTER_VALID_AMOUNT);
											return;
										}
										const boundedAmount = Math.min(Math.max(parsed, 0), maxChargeAmount);
										const formattedValue = boundedAmount.toFixed(2);
										setEnteredChargeAmount(formattedValue);
										setAmountToCharge(formattedValue);
										setAmountInputError(validateEnteredAmount(formattedValue));
									}}
								/>
							</div>
						</div>
					</div>
					{amountInputError && <div className={styles.errorMessage}>{amountInputError}</div>}
				</div>
				{/* Payment Methods */}

				<div className={styles.sidebarContainer}>
					<CustomerCreditDetails
						customerCreditDetails={customerCreditDetails}
						isLoading={isCustomerCreditLoading}
						showTopUpAction={!isMembershipPayment}
						onTopUpClick={onTopUpClick}
						hasOpenMembershipSale={Boolean(openMembershipSale)}
						openMembershipSale={openMembershipSale}
						onOpenMembershipSale={handleOpenMembershipSale}
					/>
					{insufficientCreditMessage ? (
						<div className={styles.messageContainer}>
							<p className={styles.errorMessage}>{insufficientCreditMessage}</p>
						</div>
					) : null}
				</div>

				<PaymentMethods
					amountDue={amountDue}
					isProcessingPayment={isProcessingPayment}
					setIsProcessingPayment={setIsProcessingPayment}
					enteredChargeAmount={enteredChargeAmount}
					parsedEnteredCharge={parsedEnteredCharge}
					hasValidEnteredAmount={hasValidEnteredAmount}
					setAmountInputError={setAmountInputError}
					validateEnteredAmount={validateEnteredAmount}
					onNext={onNext}
					isMembershipPayment={isMembershipPayment}
					paymentMethodsRefreshKey={paymentMethodsRefreshKey}
					setInsufficientCreditMessage={setInsufficientCreditMessage}
					hasOpenMembershipSale={Boolean(openMembershipSale)}
					onOpenMembershipSale={handleOpenMembershipSale}
				/>
				
				{!isProductSale && (amountDue === 0 || amountDue === '0.00' || amountDue === 0.00 || amountDue === '0') && (
					<>
						{completeBookingError && (
							<div className={`${styles.overlappingErrorContainer} ${styles.bottom}`}>
								<div className={styles.saleInProgressErrorRow}>
									<div className={styles.errorMessage}>{completeBookingError}</div>
									{onReload && (
										<button
											type="button"
											className={styles.reloadErrorButton}
											onClick={() => onReload(completeBookingErrorResponseRef.current)}
										>
											<ReloadIcon size={12} color="currentColor" />
											Reload
										</button>
									)}
								</div>
							</div>
						)}
						<div className={styles.bookBtnContainer}>
							<PrimaryButton
							text={isSubmitting ? "Processing..." : "Complete Booking"}
							className={`${styles.fullWidthBtn} rounded-0`}
							type="button"

							disabled={isSubmitting}
							onClick={handleCompleteBooking}
							/>
						</div>
					</>
				)}

			</div>
		</div>

	</>
	);
};

export default PaymentPage;